import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { UserSwitch, X } from '@phosphor-icons/react';
import { useAuth, formatApiErrorDetail } from '../auth/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin-only: pick a customer and switch the WHOLE session into that customer's
// real account (server-side RBAC included). A floating banner offers the way back.
const CustomerModeModal = ({ open, onClose }) => {
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    axios.get(`${API}/users`)
      .then((res) => {
        const list = (res.data || []).filter((u) => u.role === 'customer' && u.active !== false);
        list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCustomers(list);
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [open]);

  if (!open) return null;

  const switchNow = async () => {
    if (!selectedId || busy) return;
    setBusy(true);
    try {
      await impersonate(selectedId);
      onClose();
      navigate('/');
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div
        className="bg-surface border border-border-color rounded-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="customer-mode-modal"
      >
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-xl font-bold font-heading text-gold flex items-center gap-2">
            <UserSwitch size={20} weight="duotone" /> Customer mode
          </h3>
          <button onClick={onClose} data-testid="customer-mode-close" className="p-1 text-text-secondary hover:text-gold" aria-label="Close">
            <X size={18} weight="bold" />
          </button>
        </div>
        <p className="text-xs text-text-secondary mb-4">
          You&apos;ll be signed in as this customer for real — their pricing, their cart, their
          permissions. Anything you do (saving savers, submitting orders) happens as them.
          A banner will offer the way back to admin.
        </p>
        <label className="text-sm text-text-secondary mb-1.5 block">Customer</label>
        <select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          data-testid="customer-mode-select"
          className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none mb-4"
        >
          <option value="">— Select a customer —</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
          ))}
        </select>
        {error && <p className="text-xs text-danger mb-3" data-testid="customer-mode-error">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-border-color text-text-secondary rounded-lg text-sm hover:border-gold hover:text-gold"
          >
            Cancel
          </button>
          <button
            onClick={switchNow}
            disabled={!selectedId || busy}
            data-testid="customer-mode-switch-button"
            className="px-4 py-2 bg-gold text-black font-bold rounded-lg text-sm hover:bg-gold-hover disabled:opacity-50"
          >
            {busy ? 'Switching…' : 'Switch to customer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerModeModal;
